import AppContext from 'App/AppContext';
import { IPCheckbox, IPLabel, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { FluentUIPieChart, IPieChartDataPoint } from 'components/Charts/PieChart';
import PeriodFilter, { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import {
  getPeriodFilterEndDate,
  getPeriodFilterStartDate,
  getPeriodURLParams,
} from 'components/Filter/PeriodFilterHelper';
import HrefLink from 'components/Links/HrefLink';
import SingleTagPicker from 'components/Pickers/SingleTagPicker';
import { ISingleTag } from 'components/Tags/SingleTag';
import { globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import Tag from 'models/tag';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTaskTag } from 'services/Api/dashboardService';
import { apiRequest } from 'services/Auth/authConfig';
import { generatePalette } from 'utils/color';
import { fromApiDateOptional } from 'utils/datetime';
import { IWidgetConfigRendererProps } from '../WidgetConfigRenderer';
import { IWidgetRendererProps } from '../WidgetRenderer';

export class WidgetTaskTagConfig {
  tagId: number;

  showOpenTasks: boolean;

  showAllTaskLink: boolean;

  useOrgTagColors: boolean;

  period?: IPeriodFilterData;

  constructor() {
    this.tagId = 0;
    this.showOpenTasks = false;
    this.showAllTaskLink = false;
    this.useOrgTagColors = false;
  }

  load(raw: string | undefined) {
    if (raw) {
      try {
        const newRawConfig = JSON.parse(raw);
        this.tagId = newRawConfig.tagId ?? [];
        this.showAllTaskLink = newRawConfig.showAllTaskLink ?? false;
        this.useOrgTagColors = newRawConfig.useOrgTagColors ?? false;
        this.showOpenTasks = newRawConfig.showOpenTasks ?? false;
        this.period = newRawConfig.period;
      } catch {
        //ignore
      }
    }
  }

  clone(): WidgetTaskTagConfig {
    const newConfig = new WidgetTaskTagConfig();
    newConfig.tagId = this.tagId;
    newConfig.showAllTaskLink = this.showAllTaskLink;
    newConfig.useOrgTagColors = this.useOrgTagColors;
    newConfig.showOpenTasks = this.showOpenTasks;
    newConfig.period = this.period;

    return newConfig;
  }
}

interface IWidgetTaskTagProps extends IWidgetRendererProps {}

const WidgetTaskTag: FunctionComponent<IWidgetTaskTagProps> = (props: IWidgetTaskTagProps) => {
  const { t } = useTranslation(['translation', 'widgets']);
  const appContext = useContext(AppContext);
  const [tags, setTags] = useState<Tag[]>([]);
  const [tagColors, setTagColors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataCount, setDataCount] = useState<number>(0);
  const [config, setConfig] = useState<WidgetTaskTagConfig | undefined>(undefined);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        const tags = await appContext.globalDataCache.tags.getItems();
        const config = loadConfig();
        setConfig(config);

        const baseTag = tags.find((t) => t.tagId === config.tagId);
        if (baseTag) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);

          //save copy of the tags so we can change the color for the gradient
          const filterTags = tags.filter((t) => t.tagName === baseTag.tagName).map((t) => t.clone());

          //calculate start and end dates
          const customStart = fromApiDateOptional(config.period?.customStart);
          const customEnd = fromApiDateOptional(config.period?.customEnd);
          const start = getPeriodFilterStartDate(
            config.period?.option,
            config.period?.auditYear,
            customStart,
            appContext,
          );
          const end = getPeriodFilterEndDate(config.period?.option, config.period?.auditYear, customEnd, appContext);

          const data = await apiGetWidgetDataTaskTag(
            filterTags,
            config.showOpenTasks,
            start,
            end,
            accessToken,
            props.abortSignal,
          );

          setTags(data);
          if (!config.useOrgTagColors) {
            setTaskTagColors(baseTag, data);
          }
          setDataCount(data.reduce((sum, current) => sum + (current.usedCount ?? 0), 0));
        }
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTaskTagConfig => {
    const newConfig = new WidgetTaskTagConfig();
    newConfig.load(props.widget.widgetConfig);
    newConfig.tagId = appContext.globalDataCache.tags.get(newConfig.tagId).tagId;

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  const setTaskTagColors = (baseTag: Tag, tags: Tag[]) => {
    const startColor = baseTag.tagColor;
    const colors = generatePalette(tags.length, startColor);
    setTagColors(colors);
  };

  const getData = (): IPieChartDataPoint[] => {
    if (!config) return [];

    const graphData: IPieChartDataPoint[] = tags.map((s, idx) => {
      return {
        name: s.tagValue,
        value: s.usedCount ?? 0,
        color: s.tagColor,
        link: getTasksUrl(s.tagId),
      };
    });

    graphData.sort((a, b) => b.value - a.value);

    if (!config.useOrgTagColors) {
      graphData.forEach((t, idx) => (t.color = tagColors[idx]));
    }

    return graphData;
  };

  const getTasksUrl = (tagId: number) => {
    if (!config) return undefined;
    const tagStr = `?tags=${tagId}`;

    let periodStr = '';
    if (config.period) {
      periodStr = '&';
      periodStr += getPeriodURLParams(config.period);
    }

    const url = `/tasks/alltasks${tagStr}${periodStr}${config.showOpenTasks ? '&open=true' : ''}`;

    return url;
  };

  const getAllTasksUrl = () => {
    if (!config) return undefined;
    const tagStr = `?tagGroups=${config.tagId}`;

    let periodStr = '';
    if (config.period) {
      periodStr = '&';
      periodStr += getPeriodURLParams(config.period);
    }

    const url = `/tasks/alltasks${tagStr}${periodStr}${config.showOpenTasks ? '&open=true' : ''}`;

    return url;
  };

  const getHeight = (): number => {
    if (config && config.showAllTaskLink) {
      return 210;
    }

    return 250;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (dataCount === 0) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('widgets:TaskTag.NoTasks')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      <IPStack.Item grow>
        <FluentUIPieChart
          data={getData()}
          height={getHeight()}
          width={280}
          label={dataCount.toString()}
        ></FluentUIPieChart>
      </IPStack.Item>
      {config && config.showAllTaskLink && (
        <HrefLink href={getAllTasksUrl()}>{t('widgets:TaskTag.AllTaskLink')}</HrefLink>
      )}
    </IPStack>
  );
};

export default WidgetTaskTag;

//
// Config
//

interface IWidgetConfigWidgetTaskTagProps extends IWidgetConfigRendererProps {}

export const WidgetConfigTaskTag: FunctionComponent<IWidgetConfigWidgetTaskTagProps> = (
  props: IWidgetConfigWidgetTaskTagProps,
) => {
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState<ISingleTag | undefined>(undefined);
  const [showAllTasks, setShowAllTasks] = useState<boolean>(false);
  const [showOpenTasks, setShowOpenTasks] = useState<boolean>(false);
  const [useOrgTaskColors, setUseOrgTaskColors] = useState<boolean>(false);
  const [period, setPeriod] = useState<IPeriodFilterData | undefined>(undefined);
  const [currentWidgetId, setCurrentWidgetId] = useState<number>(-1);

  useEffect(() => {
    if (currentWidgetId === -1 || currentWidgetId !== props.widget.widgetId) {
      setCurrentWidgetId(props.widget.widgetId);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  const loadConfig = (): WidgetTaskTagConfig => {
    let newConfig = new WidgetTaskTagConfig();
    if (props.widget.widgetConfig) {
      newConfig = JSON.parse(props.widget.widgetConfig);
      newConfig.tagId = appContext.globalDataCache.tags.get(newConfig.tagId).tagId;
    }

    if (props.period) {
      newConfig.period = props.period; //override period with the one from props
    }

    return newConfig;
  };

  const loadData = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      await appContext.globalDataCache.tags.getItems();
      const config = loadConfig();

      if (config.tagId) {
        setSelectedTag(appContext.globalDataCache.tags.get(config.tagId));
      }

      setShowAllTasks(config.showAllTaskLink);
      setShowOpenTasks(config.showOpenTasks);
      setUseOrgTaskColors(config.useOrgTagColors);
      setPeriod(config.period);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getConfig = (
    tag: ISingleTag | undefined,
    showAllTasks: boolean,
    useOrgTaskColors: boolean,
    openTasks: boolean,
    period: IPeriodFilterData | undefined,
  ): WidgetTaskTagConfig => {
    const config = new WidgetTaskTagConfig();
    config.tagId = (tag?.tagId as number) ?? 0;
    config.showAllTaskLink = showAllTasks;
    config.useOrgTagColors = useOrgTaskColors;
    config.showOpenTasks = openTasks;
    config.period = period;

    return config;
  };

  const onAdd = (tag: ISingleTag) => {
    setSelectedTag(tag);
    const config = getConfig(tag, showAllTasks, useOrgTaskColors, showOpenTasks, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const onRemove = () => {
    setSelectedTag(undefined);
    const config = getConfig(undefined, showAllTasks, useOrgTaskColors, showOpenTasks, period);
    props.onUpdateConfig(JSON.stringify(config), false);
  };

  const onSetShowAllTasks = (checked: boolean) => {
    setShowAllTasks(checked);
    const config = getConfig(selectedTag, checked, useOrgTaskColors, showOpenTasks, period);
    props.onUpdateConfig(JSON.stringify(config), selectedTag !== undefined);
  };

  const onSetShowOpenTasks = (checked: boolean) => {
    setShowOpenTasks(checked);
    const config = getConfig(selectedTag, showAllTasks, useOrgTaskColors, checked, period);
    props.onUpdateConfig(JSON.stringify(config), selectedTag !== undefined);
  };

  const onSetUseOrgTaskColors = (checked: boolean) => {
    setUseOrgTaskColors(checked);
    const config = getConfig(selectedTag, showAllTasks, checked, showOpenTasks, period);
    props.onUpdateConfig(JSON.stringify(config), selectedTag !== undefined);
  };

  const onSetPeriod = (period: IPeriodFilterData | undefined) => {
    setPeriod(period);
    const config = getConfig(selectedTag, showAllTasks, useOrgTaskColors, showOpenTasks, period);
    props.onUpdateConfig(JSON.stringify(config), true);
  };

  const getTagKeys = (): ISingleTag[] => {
    const keys: ISingleTag[] = [];

    appContext.globalDataCache.tags.items.forEach((t) => {
      if (keys.findIndex((k) => k.tagName === t.tagName) === -1) {
        const newKey: ISingleTag = {
          tagId: t.tagId,
          tagName: t.tagName,
          tagColor: t.tagColor,
        };
        keys.push(newKey);
      }
    });

    return keys;
  };

  //
  // Main render
  //
  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <PeriodFilter
          showOption={true}
          showCurrentAuditYear={true}
          storageKey={undefined}
          onUpdatePeriodOption={onSetPeriod}
          period={period}
          showAll={true}
          disabled={props.widget.dashboard?.isContextType()}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPLabel>{t('widgets:TaskTag.Config.Label')}</IPLabel>
        <SingleTagPicker
          tags={getTagKeys()}
          itemLimit={1}
          selectedTags={selectedTag ? [selectedTag] : []}
          onAdd={onAdd}
          onRemove={onRemove}
          isLoading={isLoading}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={useOrgTaskColors}
          label={t('widgets:TaskTag.Config.OrgTaskColors')}
          onChange={(ev, checked) => {
            onSetUseOrgTaskColors(checked ?? false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={showOpenTasks}
          label={t('widgets:TaskTag.Config.LabelShowOpenTasks')}
          onChange={(ev, checked) => {
            onSetShowOpenTasks(checked ?? false);
          }}
        />
      </IPStack.Item>
      <IPStack.Item>
        <IPCheckbox
          checked={showAllTasks}
          label={t('widgets:TaskTag.Config.LabelShowAllTasks')}
          onChange={(ev, checked) => {
            onSetShowAllTasks(checked ?? false);
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
